import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import AdjustInventoryModal from '@/components/AdjustInventoryModal';
import { 
  ArrowLeft, 
  Edit2, 
  Save, 
  X, 
  Flag, 
  Trash2,
  Package,
  MapPin,
  TrendingUp,
  DollarSign,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';

interface Supply {
  supply_id: string;
  item_name: string;
  category: string;
  category_other: string | null;
  manufacturer: string;
  catalog_number: string;
  lot_number: string;
  expiration_date: string | null;
  quantity_on_hand: number;
  par_level_min: number | null;
  par_level_max: number | null;
  unit_cost: number;
  storage_location: string;
  notes: string | null;
  days_until_expiry: number | null;
  stock_status: string;
  needs_reorder: boolean;
  active_alerts_count: number;
  total_value: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

interface Transaction {
  transaction_id: string;
  transaction_type: string;
  quantity_change: number;
  quantity_after: number;
  lot_number: string | null;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  performed_at: string;
  performed_by: string | null;
  performed_by_name: string | null;
  supply_name: string | null;
}

export default function SupplyDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedSupply, setEditedSupply] = useState<Partial<Supply>>({});
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  // Fetch supply details
  const { data: supply, isLoading } = useQuery({
    queryKey: ['supply', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_surgical_supplies')
        .select('*')
        .eq('supply_id', id)
        .single();

      if (error) throw error;
      return data as Supply;
    },
    enabled: !!id,
  });

  // Fetch transaction history
  const { data: transactions = [] } = useQuery({
    queryKey: ['supply-transactions', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_supply_transactions')
        .select('*')
        .eq('supply_id', id)
        .order('performed_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Transaction[];
    },
    enabled: !!id,
  });

  // Update supply mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Supply>) => {
      const { data, error } = await supabase
        .from('or_surgical_supplies')
        .update(updates)
        .eq('supply_id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply', id] });
      queryClient.invalidateQueries({ queryKey: ['supplies'] });
      setIsEditing(false);
      setEditedSupply({});
    },
  });

  // Flag for reorder mutation
  const flagReorderMutation = useMutation({
    mutationFn: async () => {
      // Insert into or_supply_alerts
      const { data, error } = await supabase
        .from('or_supply_alerts')
        .insert({
          supply_id: id,
          alert_type: 'Reorder Flagged',
          severity: 'Info',
          message: `${supply?.item_name} flagged for reorder by user`,
          flagged_for_reorder: true,
          reorder_quantity: supply?.par_level_max || 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply', id] });
      alert('Supply flagged for reorder successfully');
    },
  });

  // Soft delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from('or_surgical_supplies')
        .update({ is_active: false })
        .eq('supply_id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      navigate('/supplies');
    },
  });

  const handleSave = () => {
    updateMutation.mutate(editedSupply);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedSupply({});
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${supply?.item_name}? This will mark it as inactive.`)) {
      deleteMutation.mutate();
    }
  };

  const handleFlagReorder = () => {
    if (window.confirm(`Flag ${supply?.item_name} for reorder?`)) {
      flagReorderMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-full bg-pt-bg flex items-center justify-center">
        <span className="pt-spinner" />
        <span className="ml-3 text-pt-text-muted">Loading supply details...</span>
      </div>
    );
  }

  if (!supply) {
    return (
      <div className="min-h-full bg-pt-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-pt-text-muted mb-4">Supply not found</p>
          <Link to="/supplies" className="text-pt-gold hover:text-pt-gold-light">
            ← Back to Supplies
          </Link>
        </div>
      </div>
    );
  }

  const getExpirationBadge = (days: number | null) => {
    if (days === null) return <span className="text-xs text-pt-text0">No expiration</span>;
    if (days < 0) return <span className="text-xs text-pt-red font-medium">Expired {Math.abs(days)}d ago</span>;
    if (days <= 30) return <span className="text-xs text-pt-amber font-medium">{days}d remaining</span>;
    if (days <= 90) return <span className="text-xs text-pt-cyan">{days}d remaining</span>;
    return <span className="text-xs text-pt-text-muted">{days}d remaining</span>;
  };

  const getStockBadge = (status: string) => {
    const styles = {
      'In Stock': 'bg-pt-green/20 text-pt-green border-pt-green/30',
      'Low Stock': 'bg-pt-amber/20 text-pt-amber border-pt-amber/30',
      'Out of Stock': 'bg-pt-red/20 text-pt-red border-pt-red/30',
      'Overstocked': 'bg-pt-cyan/20 text-pt-cyan border-pt-cyan/30',
    };
    return (
      <span className={`px-2 py-1 text-sm font-medium rounded border ${styles[status as keyof typeof styles] || 'bg-pt-text-dim/20 text-pt-text-muted border-pt-input-border'}`}>
        {status}
      </span>
    );
  };

  const getParLevelIndicator = () => {
    const qty = supply.quantity_on_hand;
    const min = supply.par_level_min || 0;
    const max = supply.par_level_max || 0;

    if (max === 0) return null;

    const percentage = Math.min((qty / max) * 100, 100);
    let color = 'bg-pt-green';
    if (qty < min) color = 'bg-pt-red';
    else if (qty < min * 1.5) color = 'bg-pt-amber';

    return (
      <div className="mt-2">
        <div className="h-2 bg-pt-dark rounded-full overflow-hidden">
          <div className={`h-full ${color}`} style={{ width: `${percentage}%` }} />
        </div>
        <div className="flex justify-between text-xs text-pt-text-muted mt-1">
          <span>Min: {min}</span>
          <span>Current: {qty}</span>
          <span>Max: {max}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-full bg-pt-bg">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/supplies"
              className="text-pt-text-muted hover:text-pt-gold transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-pt-gold font-['Rajdhani'] tracking-wide">
                {supply.item_name}
              </h1>
              <p className="text-pt-text-muted text-sm mt-1">
                {supply.manufacturer} • {supply.catalog_number}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-pt-surface border border-pt-border rounded text-pt-text hover:border-pt-gold transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => setShowAdjustModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-pt-surface border border-pt-border rounded text-pt-text hover:border-pt-gold transition-colors"
                >
                  <Package className="w-4 h-4" />
                  Adjust Inventory
                </button>
                <button
                  onClick={handleFlagReorder}
                  className="flex items-center gap-2 px-4 py-2 bg-pt-cyan/10 border border-pt-cyan/30 rounded text-pt-cyan hover:bg-pt-cyan/20 transition-colors"
                >
                  <Flag className="w-4 h-4" />
                  Flag for Reorder
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-4 py-2 bg-pt-red/10 border border-pt-red/30 rounded text-pt-red hover:bg-pt-red/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-pt-gold text-pt-bg rounded hover:bg-pt-gold-light transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 bg-pt-surface border border-pt-border rounded text-pt-text hover:border-pt-red transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main Grid - 7 Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* 1. Item Information */}
          <Section icon={<Package />} title="Item Information">
            <Field label="Item Name" value={supply.item_name} isEditing={isEditing} 
              onChange={(v) => setEditedSupply({ ...editedSupply, item_name: v })} />
            <Field label="Category" value={supply.category} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, category: v })}
              type="select" options={['Sutures', 'Dressings', 'Implants', 'Disposables', 'Instruments', 'Other']} />
            {supply.category === 'Other' && (
              <Field label="Category Other" value={supply.category_other || ''} isEditing={isEditing}
                onChange={(v) => setEditedSupply({ ...editedSupply, category_other: v })} />
            )}
            <Field label="Manufacturer" value={supply.manufacturer} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, manufacturer: v })} />
            <Field label="Catalog Number" value={supply.catalog_number} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, catalog_number: v })} />
          </Section>

          {/* 2. Inventory Status */}
          <Section icon={<TrendingUp />} title="Inventory Status">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Stock Status</label>
                <div className="mt-1">{getStockBadge(supply.stock_status)}</div>
              </div>
              <Field label="Quantity on Hand" value={supply.quantity_on_hand.toString()} isEditing={isEditing}
                onChange={(v) => setEditedSupply({ ...editedSupply, quantity_on_hand: parseInt(v) || 0 })}
                type="number" />
              {supply.needs_reorder && (
                <div className="flex items-center gap-2 text-pt-cyan text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Needs reorder</span>
                </div>
              )}
            </div>
          </Section>

          {/* 3. Lot Information */}
          <Section icon={<Clock />} title="Lot Information">
            <Field label="Lot Number" value={supply.lot_number} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, lot_number: v })} />
            <Field label="Expiration Date" value={supply.expiration_date || ''} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, expiration_date: v })}
              type="date" />
            <div>
              <label className="text-xs text-pt-text-muted uppercase tracking-wider">Expiration Status</label>
              <div className="mt-1">{getExpirationBadge(supply.days_until_expiry)}</div>
            </div>
          </Section>

          {/* 4. Storage */}
          <Section icon={<MapPin />} title="Storage Location">
            <Field label="Location" value={supply.storage_location} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, storage_location: v })} />
            <Field label="Notes" value={supply.notes || ''} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, notes: v })}
              type="textarea" />
          </Section>

          {/* 5. Par Levels */}
          <Section icon={<TrendingUp />} title="Par Levels">
            <Field label="Par Level Min" value={supply.par_level_min?.toString() || ''} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, par_level_min: parseInt(v) || null })}
              type="number" />
            <Field label="Par Level Max" value={supply.par_level_max?.toString() || ''} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, par_level_max: parseInt(v) || null })}
              type="number" />
            {!isEditing && getParLevelIndicator()}
          </Section>

          {/* 6. Costing */}
          <Section icon={<DollarSign />} title="Costing">
            <Field label="Unit Cost" value={supply.unit_cost.toFixed(2)} isEditing={isEditing}
              onChange={(v) => setEditedSupply({ ...editedSupply, unit_cost: parseFloat(v) || 0 })}
              type="number" step="0.01" prefix="$" />
            <div>
              <label className="text-xs text-pt-text-muted uppercase tracking-wider">Total Value</label>
              <div className="text-lg font-semibold text-pt-gold mt-1">
                ${supply.total_value.toFixed(2)}
              </div>
              <div className="text-xs text-pt-text-muted mt-1">
                {supply.quantity_on_hand} × ${supply.unit_cost.toFixed(2)}
              </div>
            </div>
          </Section>
        </div>

        {/* 7. Transaction History */}
        <div className="bg-pt-surface border border-pt-border rounded-lg p-6">
          <h2 className="text-xl font-bold text-pt-text mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-pt-gold" />
            Transaction History
          </h2>

          {transactions.length === 0 ? (
            <EmptyState title="No transactions recorded" hint="Receipts, usage, and adjustments for this supply will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-pt-border">
                    <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase">Date</th>
                    <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase">Type</th>
                    <th className="text-right p-3 text-xs font-semibold text-pt-text-muted uppercase">Change</th>
                    <th className="text-right p-3 text-xs font-semibold text-pt-text-muted uppercase">After</th>
                    <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase">Lot #</th>
                    <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase">Performed By</th>
                    <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.transaction_id} className="border-b border-pt-border hover:bg-pt-bg">
                      <td className="p-3 text-sm text-pt-text">
                        {new Date(txn.performed_at).toLocaleString()}
                      </td>
                      <td className="p-3 text-sm text-pt-text">{txn.transaction_type}</td>
                      <td className={`p-3 text-sm text-right font-medium ${txn.quantity_change > 0 ? 'text-pt-green' : 'text-pt-red'}`}>
                        {txn.quantity_change > 0 ? '+' : ''}{txn.quantity_change}
                      </td>
                      <td className="p-3 text-sm text-right text-pt-text">{txn.quantity_after}</td>
                      <td className="p-3 text-xs text-pt-text-muted font-mono">{txn.lot_number || '—'}</td>
                      <td className="p-3 text-sm text-pt-text-muted">{txn.performed_by_name || '—'}</td>
                      <td className="p-3 text-sm text-pt-text-muted">{txn.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Adjust Inventory Modal */}
      <AdjustInventoryModal
        supply={{
          supply_id: supply.supply_id,
          item_name: supply.item_name,
          quantity_on_hand: supply.quantity_on_hand,
        }}
        isOpen={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
      />
    </div>
  );
}

// Helper Components
function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-pt-surface border border-pt-border rounded-lg p-6">
      <h2 className="text-lg font-bold text-pt-text mb-4 flex items-center gap-2">
        <span className="text-pt-gold">{icon}</span>
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  isEditing: boolean;
  onChange?: (value: string) => void;
  type?: 'text' | 'number' | 'date' | 'textarea' | 'select';
  step?: string;
  prefix?: string;
  options?: string[];
}

function Field({ label, value, isEditing, onChange, type = 'text', step, prefix, options }: FieldProps) {
  return (
    <div>
      <label className="text-xs text-pt-text-muted uppercase tracking-wider">{label}</label>
      {isEditing ? (
        type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            className="mt-1 w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-sm text-pt-text focus:outline-none focus:border-pt-gold"
            rows={3}
          />
        ) : type === 'select' ? (
          <select
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            className="mt-1 w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-sm text-pt-text focus:outline-none focus:border-pt-gold"
          >
            {options?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <div className="relative mt-1">
            {prefix && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-pt-text-muted">{prefix}</span>
            )}
            <input
              type={type}
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              step={step}
              className={`w-full bg-pt-bg border border-pt-border rounded ${prefix ? 'pl-7' : 'pl-3'} pr-3 py-2 text-sm text-pt-text focus:outline-none focus:border-pt-gold`}
            />
          </div>
        )
      ) : (
        <div className="mt-1 text-sm text-pt-text">{prefix}{value || '—'}</div>
      )}
    </div>
  );
}
